/* eslint-disable sort-keys */
import { Clickable, H2, IconLockup, P, Section } from '@ahmdigital/ui';
import { upperFirst } from 'lodash/fp';
import getOpeningHours from '@ahmdigital/logic/lib/utils/get-opening-hours';

import { URLS } from '../../../../models/url/constants';
import constants from '../../../../ahm-constants';
import PreLineParagraph from '../../components/pre-line-paragraph';
import styles from './styles.module.sass';

const LINKS_BY_TYPE = {
  car: {
    contactNumber: constants.CONTACT.PHONE.CAR_INSURANCE,
    email: constants.CONTACT.EMAIL.GI_CAR_CLAIMS_EMAIL,
    emergencyNumber: constants.CONTACT.PHONE.CAR_INSURANCE_EMERGENCY,
    formsAndGuides: URLS.HELP.CAR_INSURANCE_FORMS_AND_GUIDES,
    login: constants.URLS.SALES.CAR_INSURANCE_LOGIN,
  },
  home: {
    contactNumber: constants.CONTACT.PHONE.HOME_INSURANCE,
    email: constants.CONTACT.EMAIL.GI_HOME_CLAIMS_EMAIL,
    emergencyNumber: constants.CONTACT.PHONE.HOME_INSURANCE_EMERGENCY,
    formsAndGuides: URLS.HELP.HOME_INSURANCE_FORMS_AND_GUIDES,
    login: constants.URLS.SALES.HOME_INSURANCE_LOGIN,
  },
};

const getLifeAndIncomeContent = ({ insuranceType }: { insuranceType: string }) => {
  // @ts-expect-error - Automatic, Please fix when editing this file
  const contentLinks = LINKS_BY_TYPE[insuranceType];
  const typeText = upperFirst(insuranceType);
  return {
    meta: {
      title: `Contact us - ahm ${typeText} Insurance`,
      description: `Have questions about ahm insurance policies or quotes? Contact us via phone or email today for help with ahm ${typeText} insurance queries.`,
    },
    banner: {
      title: 'Contact us',
      preHeading: `ahm ${typeText} Insurance`,
    },
    body: (
      <>
        <Section className={styles.sectionWrapper}>
          <H2 looksLike="h3">General enquiries</H2>
          <P>For all enquiries:</P>
          <IconLockup className={styles.iconWrapper} icon={{ name: 'speech-bubble' }}>
            <P looksLike="h4">
              <Clickable inNewTab to={URLS.THIRD_PARTY.CAR_AND_HOME_CONTACT}>
                Message us
              </Clickable>
            </P>
            <PreLineParagraph>{getOpeningHours(constants.AHM_SERVICE_NAME.CAR_AND_HOME)}</PreLineParagraph>
          </IconLockup>
          <H2 looksLike="h3">Claim enquiries</H2>
          <P>For help submitting a claim, or any questions on an existing claim:</P>
          <IconLockup className={styles.iconWrapper} icon={{ name: 'phone-circle' }}>
            <P looksLike="h4">
              <Clickable
                to={`tel:
              ${contentLinks.contactNumber}`}
              >
                {contentLinks.contactNumber}
              </Clickable>
            </P>
            <PreLineParagraph>{getOpeningHours(constants.AHM_SERVICE_NAME.CAR_AND_HOME)}</PreLineParagraph>
          </IconLockup>
          <IconLockup icon={{ name: 'device-desktop' }}>
            <P looksLike="h4">Email</P>
            <P>
              <Clickable to={`mailto:${contentLinks.email}`}>{contentLinks.email}</Clickable>
            </P>
          </IconLockup>
        </Section>
        <Section className={styles.sectionWrapper}>
          <H2 looksLike="h3">24/7 emergency assistance</H2>
          <P>Within Australia and overseas</P>
          <IconLockup icon={{ name: 'phone-circle' }}>
            <P looksLike="h4">
              <Clickable
                to={`tel:
              ${contentLinks.emergencyNumber}`}
              >
                {contentLinks.emergencyNumber}
              </Clickable>
            </P>
          </IconLockup>
        </Section>

        <Section className={styles.sectionWrapper}>
          <H2 looksLike="h3">Need to make a claim?</H2>
          <P>
            <Clickable to={contentLinks.login}>Log in</Clickable> to your account to get in touch with our contact
            centre.
          </P>
        </Section>

        <Section className={styles.sectionWrapper}>
          <H2 looksLike="h3">Forms and guides</H2>
          <P>
            Read our <Clickable to={contentLinks.formsAndGuides}>forms and guides</Clickable> for the Product Disclosure
            Statement (PDS), Financial Services Guide (FSG) and Target Market Determination (TMD).
          </P>
        </Section>

        <Section className={styles.sectionWrapper}>
          <H2 looksLike="h3">How to make a complaint</H2>
          <P>
            We always aim to do the right thing by our customers. If you feel something isn’t right or if we’re not
            meeting your expectations, we’d like the opportunity to resolve it.
          </P>
          <P>
            We have a complaint handling system in place to resolve complaints fairly and efficiently. You can read more
            about it in the{' '}
            <Clickable inNewTab to={URLS.THIRD_PARTY.CAR_AND_HOME_INSURANCE_COMPLAINTS}>
              Complaint Handling Policy
            </Clickable>
            .
          </P>
        </Section>
        <Section className={styles.sectionWrapper}>
          <H2 looksLike="h3">Extra Care and Support</H2>
          <P>
            We’re here to help if you need assistance, for example if you are experiencing financial hardship, facing
            difficulties arising from domestic family violence or other vulnerable situations. Where practical, we can
            also provide assistance with translation and interpreting services. You can read more about it in the{' '}
            <Clickable inNewTab to={URLS.THIRD_PARTY.CAR_AND_HOME_CARE_AND_SUPPORT}>
              Domestic and Family Violence and Financial Hardship policy
            </Clickable>
            .
          </P>
        </Section>
      </>
    ),
  };
};

export default getLifeAndIncomeContent;
